import { concepts, lessonById, lessons, wordById } from '../data/foundations'
import type { Lesson } from '../types/content'
import type { EvidenceCounters, LearningSkill, Progress } from '../types/progress'

export interface ReviewTask {
  lessonId: string
  conceptId: string
  wordId?: string
  dueAt: string
  reason: 'due' | 'support'
}

export const localLearningDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
export const addLearningDays = (date: Date, days: number) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return localLearningDate(next)
}

const conceptById = new Map(concepts.map((concept) => [concept.id, concept]))
const requiresWord = (lesson: Lesson) => ['segment', 'encode', 'word-challenge', 'decode', 'blend'].includes(lesson.type) || (lesson.type === 'review' && lesson.mode !== 'oral_only')
const safeWord = (wordId: string, order: number) => {
  const word = wordById.get(wordId)
  return word && !word.teacherSupported && !word.isPseudoWord && word.decodableAfterConcepts.every((id) => (conceptById.get(id)?.order ?? Infinity) <= order)
}

/** One task per encountered concept. Historical scores never imply independent success. */
export function getReviewQueue(progress: Progress, now = new Date()): ReviewTask[] {
  const today = localLearningDate(now)
  const frontier = conceptById.get(progress.currentConceptId)?.order ?? concepts[0].order
  const records = progress.learningRecords?.concepts ?? {}
  const encountered = new Set([...Object.keys(records), ...progress.completedLessons.map((id) => lessonById.get(id)?.conceptId).filter((id): id is string => Boolean(id))])
  const queue: ReviewTask[] = []
  for (const conceptId of encountered) {
    const concept = conceptById.get(conceptId)
    if (!concept || concept.order > frontier) continue
    const record = records[conceptId]
    const needsSupport = record?.needsSupport ?? true
    const dueAt = record?.nextReviewDate ?? today
    if (!needsSupport && dueAt > today) continue
    const candidates = lessons.filter((lesson) => lesson.conceptId === conceptId && lesson.type !== 'hear' && (lesson.type !== 'blend' || record?.lastSkill === 'blending'))
    const preferred = record && lessonById.get(record.lastLessonId)
    if (preferred && candidates.some((lesson) => lesson.id === preferred.id)) candidates.sort((a, b) => Number(b.id === preferred.id) - Number(a.id === preferred.id))
    const supportSkill = record?.supportSkills?.[0]
    const supportType: Partial<Record<LearningSkill, Lesson['type']>> = { mapping: 'recognition', sound_discrimination: 'recognition', encoding: 'encode', word_recognition: 'word-challenge', segment_count: 'segment', segment_sequence: 'segment', decoding: 'decode', blending: 'blend' }
    if (supportSkill && supportType[supportSkill]) candidates.sort((a, b) => Number(b.type === supportType[supportSkill]) - Number(a.type === supportType[supportSkill]))
    const available = candidates.map((lesson) => {
      const wordIds = [...(record?.lastWordId ? [record.lastWordId] : []), ...lesson.wordIds, ...concept.exampleWordIds]
      return { lesson, wordId: wordIds.find((id) => safeWord(id, concept.order)) }
    }).find(({ lesson, wordId }) => !requiresWord(lesson) || wordId)
    if (!available) continue
    queue.push({ lessonId: available.lesson.id, conceptId, wordId: requiresWord(available.lesson) ? available.wordId : undefined, dueAt, reason: needsSupport ? 'support' : 'due' })
  }
  return queue.sort((a, b) => Number(b.reason === 'support') - Number(a.reason === 'support') || a.dueAt.localeCompare(b.dueAt) || conceptById.get(a.conceptId)!.order - conceptById.get(b.conceptId)!.order)
}

export const learningSkillLabels: Record<LearningSkill, string> = {
  sound_discrimination: '听辨声音', mapping: '听音认字', blending: '合成声音', segment_count: '数出声音',
  segment_sequence: '按顺序拆音', encoding: '听音拼写', word_recognition: '听词选拼写', decoding: '独立读词（成人核验）',
}

export interface LearningSummary {
  independentSuccesses: number
  supportNeeded: number
  reviewDue: number
  participation: number
  independentlyDecodedWords: number
  transferSuccesses: number
  encodingTransferSuccesses: number
  skills: (EvidenceCounters & { skill: LearningSkill; label: string })[]
  independentConceptIds: string[]
  supportConceptIds: string[]
}

export function getLearningSummary(progress: Progress, now = new Date()): LearningSummary {
  const records = progress.learningRecords ?? { concepts: {}, skills: {}, words: {} }
  const skills = (Object.entries(learningSkillLabels) as [LearningSkill, string][]).map(([skill, label]) => ({
    skill, label, independentCorrect: 0, independentIncorrect: 0, supportedCorrect: 0, supportedAttempts: 0, participation: 0, ...records.skills[skill],
  }))
  const supportConceptIds = Object.entries(records.concepts).filter(([, record]) => record.needsSupport).map(([id]) => id)
  return {
    independentSuccesses: skills.reduce((sum, skill) => sum + skill.independentCorrect, 0),
    supportNeeded: supportConceptIds.length,
    reviewDue: getReviewQueue(progress, now).length,
    participation: skills.reduce((sum, skill) => sum + skill.participation, 0),
    independentlyDecodedWords: Object.entries(records.words).filter(([id, word]) => word.independentDecodingCorrect > 0 && !wordById.get(id)?.isPseudoWord).length,
    transferSuccesses: Object.values(records.words).reduce((sum, word) => sum + word.transferCorrect, 0),
    encodingTransferSuccesses: Object.values(records.words).reduce((sum, word) => sum + (word.encodingTransferCorrect ?? 0), 0),
    skills,
    independentConceptIds: Object.entries(records.concepts).filter(([, record]) => record.independentCorrect > 0 && !record.needsSupport).map(([id]) => id),
    supportConceptIds,
  }
}
